import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types";
import { getRealmFromMarketplace } from "./marketplaceUtils";
import { isAccountingEventsGroup } from "./typeChecks";
import { FLOW_TYPES } from "./flowTypeUtils";
import { Stage } from "../lusca-search-api/generated-src";

const stageToAccountId: Record<Stage, string> = {
  [Stage.Beta]: "325864189811",
  [Stage.Gamma]: "239039480348",
  [Stage.Prod]: "800989574994",
};

const TIME_RANGE = {
  CBE: 30,
  DEFAULT: 5,
};

/**
 * The accounting-record-event-translator and accounting-record-publisher log groups
 * were deployed on this date. Queries for events before this date should exclude
 * these log groups to prevent CloudWatch "log group does not exist" errors.
 */
const ARK_LOG_GROUPS_CUTOFF_DATE = new Date("2025-06-12T00:00:00Z");

const minutesToMs = (minutes: number) => minutes * 60 * 1000;

const getStagePrefix = (stage: Stage): string => stage.toLowerCase();

const getLogGroupName = (stage: Stage, lambdaName: string): string =>
  `/aws/lambda/${getStagePrefix(stage)}-${lambdaName}`;

const getBaseQueryFilters = (): string => `
| filter @message not like /CloudWatchMetrics/
| filter @message not like /contractToBillingDate/
| filter class not like /com.amazon.variable.metrics.EmbeddedProjectableMetrics/`;

const calculateDateRange = (
  eventDate: Date,
  timeRangeMinutes: number
): [Date, Date] => {
  const startTime = new Date(
    eventDate.getTime() - minutesToMs(timeRangeMinutes)
  );
  const endTime = new Date(eventDate.getTime() + minutesToMs(timeRangeMinutes));
  return [startTime, endTime];
};

// Personal dev stacks use a "dev-<alias>" stage and all live in the shared dev
// account, so Merlon/console links resolve there instead of to an undefined
// account when developing against a dev deployment.
const DEV_ACCOUNT_ID = "758248974799";

const getAwsAccountId = (stage: Stage): string => {
  return (
    stageToAccountId[stage] ??
    (String(stage).startsWith("dev-") ? DEV_ACCOUNT_ID : "")
  );
};

export const generateDirectCloudWatchUrl = (
  logGroups: string[],
  query: string,
  startTime?: Date,
  endTime?: Date
) => {
  const region = "us-east-1";
  const encodeSpecial = (str: string) => {
    if (!str) return "";
    return str.replace(/[^a-zA-Z0-9]/g, (char) => {
      const code = char.charCodeAt(0).toString(16).padStart(2, "0");
      return `*${code}`;
    });
  };

  const encodedLogGroups = logGroups
    .filter((group) => group)
    .map((group) => `~'${encodeSpecial(group)}`)
    .join("");

  const encodedQuery = encodeSpecial(query || "");

  let timeComponent: string;
  if (startTime && endTime) {
    try {
      const encodedStartTime = encodeURIComponent(
        startTime.toISOString()
      ).replace(/%/g, "*");
      const encodedEndTime = encodeURIComponent(endTime.toISOString()).replace(
        /%/g,
        "*"
      );
      timeComponent = `end~'${encodedEndTime}~start~'${encodedStartTime}~timeType~'ABSOLUTE`;
    } catch (error) {
      const endSeconds = 0;
      const startSeconds = -300;
      timeComponent = `end~${endSeconds}~start~${startSeconds}~timeType~'RELATIVE`;
    }
  } else {
    const endSeconds = 0;
    const startSeconds = -300;
    timeComponent = `end~${endSeconds}~start~${startSeconds}~timeType~'RELATIVE`;
  }

  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:logs-insights$3FqueryDetail$3D~(${timeComponent}~tz~'UTC~unit~'seconds~editorString~'${encodedQuery}~source~(${encodedLogGroups})~lang~'CWLI)`;
};

export const getEventDateRange = (
  event:
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
    | AccountingEventsGroup
    | CanonicalBusinessEvent
    | undefined,
  isCbe: boolean
): [Date, Date] => {
  let eventDate: Date;

  if (!event) {
    // If event is undefined, use current date
    eventDate = new Date();
  } else if ("creationDate" in event && event.creationDate) {
    eventDate = new Date(event.creationDate);
  } else if (
    "body" in event &&
    event.body &&
    "header" in event.body &&
    event.body.header.transactionDate
  ) {
    eventDate = new Date(event.body.header.transactionDate);
  } else if ("accountingEvents" in event) {
    const dates = Object.values(event.accountingEvents).map(
      (e) => new Date(e.creationDate)
    );
    eventDate = new Date(Math.max(...dates.map((d) => d.getTime())));
  } else {
    eventDate = new Date(); // fallback to current date if no valid date found
  }

  const timeRange = isCbe ? TIME_RANGE.CBE : TIME_RANGE.DEFAULT;
  return calculateDateRange(eventDate, timeRange);
};

export const getDlqDateRange = (timestamp: string): [Date, Date] => {
  const eventDate = new Date(`${timestamp.replace(" ", "T")}Z`);
  return calculateDateRange(eventDate, TIME_RANGE.DEFAULT);
};

export const getEventLogGroups = (
  stage: Stage,
  eventData?: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[],
  flowType?: string,
  startTime?: Date
) => {
  const isBeforeArkCutoff = startTime && startTime < ARK_LOG_GROUPS_CUTOFF_DATE;

  const baseAccountingEventLogGroups = [
    getLogGroupName(stage, "payment-event-translator"),
    getLogGroupName(stage, "tvod-royalty-event-translator"),
    getLogGroupName(stage, "tvod-revenue-event-translator"),
    getLogGroupName(stage, "revenue-event-translator"),
  ];

  const baseDocumentLogGroups = [
    getLogGroupName(stage, "document-builder"),
    getLogGroupName(stage, "document-publisher"),
  ];

  const accountingEventLogGroups = isBeforeArkCutoff
    ? baseAccountingEventLogGroups
    : [
        ...baseAccountingEventLogGroups,
        getLogGroupName(stage, "accounting-record-event-translator"),
        getLogGroupName(stage, "accounting-record-publisher"),
      ];

  const documentLogGroups = isBeforeArkCutoff
    ? baseDocumentLogGroups
    : [
        ...baseDocumentLogGroups,
        getLogGroupName(stage, "accounting-record-publisher"),
      ];

  if (!eventData) {
    return [...new Set([...accountingEventLogGroups, ...documentLogGroups])];
  }

  // If flowType is provided, use it to determine log groups
  if (flowType) {
    if (flowType === FLOW_TYPES.ACCOUNTING) {
      return accountingEventLogGroups;
    }
    if (flowType === FLOW_TYPES.DOCUMENT) {
      return documentLogGroups;
    }
  }

  if (eventData.length > 0 && isAccountingEventsGroup(eventData[0])) {
    return accountingEventLogGroups;
  }
  return documentLogGroups;
};

export const publisherLogGroups = (stage: Stage): string[] => {
  return [
    getLogGroupName(stage, "usl-journal-entry-publisher-USAmazon"),
    getLogGroupName(stage, "usl-journal-entry-publisher-EUAmazon"),
    getLogGroupName(stage, "usl-journal-entry-publisher-JPAmazon"),
    getLogGroupName(stage, "race-publisher-usamazon"),
    getLogGroupName(stage, "race-publisher-euamazon"),
    getLogGroupName(stage, "race-publisher-jpamazon"),
  ];
};

export const getCbeLogGroups = (marketplaceId: string, stage: Stage) => {
  const realm = getRealmFromMarketplace(marketplaceId);
  const baseGroups = [getLogGroupName(stage, "accounting-lambda")];

  switch (realm) {
    case "na":
      return [
        ...baseGroups,
        getLogGroupName(stage, "usl-journal-entry-publisher-USAmazon"),
        getLogGroupName(stage, "race-publisher-usamazon"),
      ];
    case "eu":
      return [
        ...baseGroups,
        getLogGroupName(stage, "usl-journal-entry-publisher-EUAmazon"),
        getLogGroupName(stage, "race-publisher-euamazon"),
      ];
    case "fe":
      return [
        ...baseGroups,
        getLogGroupName(stage, "usl-journal-entry-publisher-JPAmazon"),
        getLogGroupName(stage, "race-publisher-jpamazon"),
      ];
    default:
      return baseGroups;
  }
};

export const getQuery = (id: string, isDlq: boolean) => {
  const baseFilters = getBaseQueryFilters();

  if (isDlq) {
    return `fields @timestamp, contextMap.AWSRequestId, exception.class, message, exception.message, exception.stackTrace
| filter @message like /${id}/ or contextMap.SQSMessageId='${id}'${baseFilters}
| sort @timestamp desc
| limit 10000`;
  }

  const baseQuery = `fields @timestamp, @entity.Attributes.Lambda.Function as LambdaFunction, message${baseFilters}`;

  const filterQuery = `| filter @message like /${id}/
    or contextMap.OrderId = '${id}'
    or contextMap.AUID = '${id}'`;

  const sortAndLimitQuery = `| sort LambdaFunction, @timestamp desc
| limit 1000`;

  return `${baseQuery}
${filterQuery}
${sortAndLimitQuery}`;
};

const dlqToLogGroupMapping: { [key: string]: string } = {
  "tvod-revenue-event-translator-intake-dlq": "tvod-revenue-event-translator",
  "revenue-event-translator-intake-dlq": "revenue-event-translator",
  "tvod-royalty-event-translator-intake-dlq": "tvod-royalty-event-translator",
  "royalty-event-translator-intake": "royalty-event-translator",
  "payment-event-translator-intake-dlq": "payment-event-translator",
  "document-builder-intake-dlq": "document-builder",
  "subs-accounting-service-intake-dlq": "accounting-lambda",
  "tvod-accounting-service-intake-dlq": "accounting-lambda",
  "accounting-service-intake-dlq": "accounting-lambda",
  "payment-accounting-service-intake-dlq": "accounting-lambda",
  "race-publisher-jpamazon-intake-dlq": "race-publisher-jpamazon",
  "race-publisher-euamazon-intake-dlq": "race-publisher-euamazon",
  "race-publisher-usamazon-intake-dlq": "race-publisher-usamazon",
  "usl-journal-entry-publisher-jpamazon-intake-dlq":
    "usl-journal-entry-publisher-JPAmazon",
  "usl-journal-entry-publisher-euamazon-intake-dlq":
    "usl-journal-entry-publisher-EUAmazon",
  "usl-journal-entry-publisher-usamazon-intake-dlq":
    "usl-journal-entry-publisher-USAmazon",
  "accounting-record-event-translator-intake-dlq":
    "accounting-record-event-translator",
  "accounting-record-publisher-intake-dlq": "accounting-record-publisher",
};

export const getDlqLogGroup = (
  eventsourcekey: string,
  stage: Stage
): string => {
  const keyWithoutStage = eventsourcekey.replace(/^(prod-|gamma-|beta-)/, "");
  const lambdaName = dlqToLogGroupMapping[keyWithoutStage];
  return getLogGroupName(stage, lambdaName);
};

const generateMerlonUrl = (stage: Stage, destinationUrl: string): string => {
  const accountId = getAwsAccountId(stage);
  const stagePrefix = getStagePrefix(stage);
  const opsRoleArn = `arn:aws:iam::${accountId}:role/${stagePrefix}-ops-role`;
  const accountName = `pv-accounting-${stagePrefix}`;
  const encodedDestinationUrl = encodeURIComponent(destinationUrl);

  return `https://iad.merlon.amazon.dev/console?awsAccountId=${accountId}&awsPartition=aws&accountName=${accountName}&sessionDuration=3600&iamRole=${opsRoleArn}&destination=${encodedDestinationUrl}`;
};

export const generateRedriveExecutionUrl = (
  stage: Stage,
  executionId: string
): string => {
  const region = "us-east-1";
  const accountId = getAwsAccountId(stage);
  const stagePrefix = getStagePrefix(stage);

  const executionArn = `arn:aws:states:${region}:${accountId}:execution:${stagePrefix}-orderredrivestatemachine:${executionId}`;
  const stepFunctionUrl = `https://${region}.console.aws.amazon.com/states/home?region=${region}#/executions/details/${encodeURIComponent(
    executionArn
  )}`;

  return generateMerlonUrl(stage, stepFunctionUrl);
};

export const generateExecutionUrlFromStepFunction = (
  stage: Stage,
  executionArn: string
): string => {
  const region = "us-east-1";
  const stepFunctionUrl = `https://${region}.console.aws.amazon.com/states/home?region=${region}#/executions/details/${encodeURIComponent(
    executionArn
  )}`;

  return generateMerlonUrl(stage, stepFunctionUrl);
};

// Build a Merlon-wrapped S3 console link for an `s3://bucket/key` URI. Returns
// null if the URI isn't a parseable s3:// path.
export const generateS3ObjectUrl = (
  stage: Stage,
  s3Uri: string
): string | null => {
  if (!s3Uri.startsWith("s3://")) {
    return null;
  }
  const path = s3Uri.slice("s3://".length);
  const slash = path.indexOf("/");
  if (slash <= 0) {
    return null;
  }
  const bucket = path.slice(0, slash);
  const key = path.slice(slash + 1);
  const region = "us-east-1";
  const s3Url = `https://${region}.console.aws.amazon.com/s3/object/${bucket}?region=${region}&prefix=${encodeURIComponent(
    key
  )}`;

  return generateMerlonUrl(stage, s3Url);
};

// Links to the S3 *bucket browser* at a prefix (folder), unlike generateS3ObjectUrl
// which targets a single object. Use for "s3://bucket/prefix/" locations.
export const generateS3PrefixUrl = (
  stage: Stage,
  s3Uri: string
): string | null => {
  if (!s3Uri.startsWith("s3://")) {
    return null;
  }
  const path = s3Uri.slice("s3://".length);
  const slash = path.indexOf("/");
  if (slash <= 0) {
    return null;
  }
  const bucket = path.slice(0, slash);
  const prefix = path.slice(slash + 1);
  const region = "us-east-1";
  const s3Url = `https://${region}.console.aws.amazon.com/s3/buckets/${bucket}?region=${region}&prefix=${encodeURIComponent(
    prefix
  )}`;

  return generateMerlonUrl(stage, s3Url);
};

// MCM lives on the new website (mcm.amazon.dev). Non-prod redrives validate
// against beta MCM (see McmModule.isBeta), so their CM is on beta.mcm.amazon.dev.
export const generateMcmUrl = (stage: Stage, mcmId: string): string => {
  const host = stage === Stage.Prod ? "mcm.amazon.dev" : "beta.mcm.amazon.dev";
  return `https://${host}/cms/${mcmId}`;
};

export const generateMerlonLogsUrl = (
  stage: Stage,
  logGroups: string[],
  query: string,
  startTime?: Date,
  endTime?: Date
) => {
  const cloudWatchUrl = generateDirectCloudWatchUrl(
    logGroups,
    query,
    startTime,
    endTime
  );

  return generateMerlonUrl(stage, cloudWatchUrl);
};
